import { useReportModal } from '@hooks/useReportModal';
import BaseModalContent from '../BaseReportModalContent/BaseReportModalContetnt';
import { REPORT_MODAL_NAMES } from 'config/labels';
import {
  LesionData,
  PlaqueVesselData,
  VesselData,
} from './PlaqueReportModalContent';
import PlaqueReportModalContent from './PlaqueReportModalContent';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { configStore } from 'config/configStore';
import useModalActions from '@hooks/common/useModalAction';
import {
  ContextKey,
  CPRContext,
  SurfaceContext,
  useBufferPool,
  VRContext,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { BranchInfo } from 'utils/FunctionUtilites';
import {
  BufferData,
  cprlesioncapture,
  imageUrlToArrayBufferSimple,
  screenshotElement,
  captureSurface3D,
  cropToSquare,
} from 'utils/captureUtils';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { useAutoCaptureModal } from '../AutoCaptureModal/AutoCaptureModal';
import { useBranchInfo } from '@contexts/BranchContext';
import { CalciumAnalysisResult } from '@models/Parameter';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import useAuth from '@hooks/Auth/useAuth';
import { getConfiguredCprCrossSectionImageMode } from '@features/analysis/rendering/Panels/cprImageModeConfig';
import { captureServerCprLesionImages } from '../serverCprReportCapture';
import { nodeNames } from 'config/render3DConfig';
import printIconUrl from 'img/print-solid.svg';
import styles from './PlaqueReportModalContent.module.scss';
import { createPlaqueAnalysisPageImageUrls } from './plaquePdfExport';
import type { PlaquePdfPatientInfo } from './plaquePdfExport';

type PlaqueReportViewMode = 'classic' | 'preview';

type VisibilityActor = {
  getVisibility?: () => boolean;
  setVisibility?: (visible: boolean) => void;
  modified?: () => void;
};

const REPORT_3D_CAPTURE_HIDDEN_KEY_PARTS = [
  nodeNames.node,
  nodeNames.centerLine,
  nodeNames.singleCenterLine,
  'centerline',
];

function shouldHideForReport3DCapture(actorKey: string) {
  const key = actorKey.toLowerCase();
  return REPORT_3D_CAPTURE_HIDDEN_KEY_PARTS.some((part) =>
    key.includes(part.toLowerCase()),
  );
}

function hideReport3DOverlayActors(context: SurfaceContext | VRContext) {
  const backups: Array<{ actor: VisibilityActor; visible: boolean }> = [];
  const seen = new Set<VisibilityActor>();

  Object.entries(context.actorIds ?? {}).forEach(([key, actor]) => {
    const visibilityActor = actor as VisibilityActor | undefined;
    if (
      !visibilityActor ||
      seen.has(visibilityActor) ||
      !shouldHideForReport3DCapture(key) ||
      typeof visibilityActor.getVisibility !== 'function' ||
      typeof visibilityActor.setVisibility !== 'function'
    ) {
      return;
    }

    backups.push({
      actor: visibilityActor,
      visible: visibilityActor.getVisibility(),
    });
    visibilityActor.setVisibility(false);
    visibilityActor.modified?.();
    seen.add(visibilityActor);
  });

  if (backups.length) {
    context.renderWindow.render();
  }

  return () => {
    backups.forEach(({ actor, visible }) => {
      actor.setVisibility?.(visible);
      actor.modified?.();
    });
    if (backups.length) {
      context.renderWindow.render();
    }
  };
}

function escapePrintAttribute(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const PlaqueReportModal = ({ onClose }: { onClose: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);
  const names = REPORT_MODAL_NAMES.plaque;
  const { contextPool, bufferPool } = useBufferPool();
  const { selectedSeriesId, selectedSeries } = useBrowserPage();
  const [selectedindex, setselectedindex] = useState<number>(0);
  const { waitingAction, closeLoadingModal, successAction, errorAction } = useModalActions();
  const { analysisType } = usePanelContainer();
  const AutoCaptureModalOpen = useAutoCaptureModal();
  const { mainBranchIds } = useBranchInfo();
  const { currentVOI, selectedLayoutConfig } = useAnalysisPage();
  const { accessToken } = useAuth();

  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;

  const branchdataset = useMemo(() => {
    const raw = pool?.branchGraphDataset?.data ?? null;
    return raw instanceof BranchInfo ? raw : null;
  }, [pool?.branchGraphDataset?.data]);

  const poolRef = useRef(contextPool);
  useEffect(() => {
    poolRef.current = contextPool;
  }, [contextPool]);

  function round(value: number | undefined, precision = 1) {
    if (value == null) return null;
    return Math.round(value * 10 ** precision) / 10 ** precision;
  }
  const safe = (v: number | undefined) =>
    typeof v === 'number' ? round(v) : null;
  const buildMainTableData = (
    data: ReturnType<BranchInfo['getPlaqueTotalInfo']>,
  ): VesselData => ({
    'Length (mm)': safe(data?.lengthValue),
    'Lumen Volume (mm³)': safe(data?.lumenValue),
    'Plaque Volume (mm³)': safe(data?.plaqueValue),
    'Vessel Volume (mm³)': safe(data?.vesselValue),
    'Calcified Plaque Volume (mm³, %)':
      data?.calcifiedValue != null && data?.vesselValue
        ? `${round(data.calcifiedValue)} (${round((data.calcifiedValue / data.vesselValue) * 100)})`
        : '-',
    'Noncalcified Plaque Volume (mm³, %)':
      data?.noncalcifiedValue != null && data?.vesselValue
        ? `${round(data.noncalcifiedValue)} (${round((data.noncalcifiedValue / data.vesselValue) * 100)})`
        : '-',
    'Low Attenuation Plaque Volume (mm³, %)':
      data?.lowattenuationValue != null && data?.vesselValue
        ? `${round(data.lowattenuationValue)} (${round((data.lowattenuationValue / data.vesselValue) * 100)})`
        : '-',
    'Percent Atheroma Volume (%)':
      data?.plaqueburden != null ? round(data.plaqueburden * 100) : null,
    'Remodeling Index': '-',
    // 'Stenosis Degree (%)': '-',
    'Minimum Lumen Area (mm²)': '-',
    'Minimum Lumen Diameter (mm)': '-',
    'Plaque Burden': '-',
  });

  // 비동기 계산 상태
  const [dataset, setDataset] = useState<PlaqueVesselData[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reportViewMode, setReportViewMode] = useState<PlaqueReportViewMode>('classic');
  const [previewPageUrls, setPreviewPageUrls] = useState<string[]>([]);
  const [isPreparingPreview, setIsPreparingPreview] = useState<boolean>(false);

  function rotateImage(src: string): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(src);
          return;
        }
        // console.log(img.height, img.width);
        canvas.width = img.height;
        canvas.height = img.width;

        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((-90 * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        resolve(canvas.toDataURL());
      }
    })
  };
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setIsLoading(true);
        waitingAction({ message: 'Generating plaque report…' });

        const status = configStore.get().plaqueStatus;
        if (!branchdataset || !pool) {
          if (!cancelled) setDataset(null);
          return;
        }

        // CPR 컨텍스트 (필요 시 key 조정)
        const con = {
          current: poolRef.current[
            `CPR.0.straightened` as ContextKey
          ] as CPRContext,
        } as React.MutableRefObject<CPRContext | null>;

        // ------- 순차 실행: vessel 단계부터 lesion 단계까지 모두 직렬 -------
        const plaquedatas: PlaqueVesselData[] = [];

        const infodata = pool?.parameter.data as CalciumAnalysisResult;
        const newtype = infodata.SavedInfo.Coronary.newType;
        let maindata: PlaqueVesselData = {
          vesselName: 'Main branch',
          lesions: [],
        };
        const sample = ['RCA', 'LAD', 'LCX'];
        for (let i = 0; i < mainBranchIds.length; i++) {
          const idx = mainBranchIds[i];
          let leslist: [number, number][] = [];
          // leslist.push([0, branchdataset.getBranchCount() - 1])
          leslist.push([0, 0]);
          const nameTemp = newtype ? newtype[idx][0] : sample[i];

          const datamain = branchdataset.getPlaqueTotalInfo(
            idx,
            nameTemp === 'LAD' || nameTemp === 'LCX'
              ? branchdataset.getLMCount()
              : 0,
          );
          const serverImg0 =
            getConfiguredCprCrossSectionImageMode() === 'server'
              ? await captureServerCprLesionImages({
                  accessToken,
                  seriesId: selectedSeriesId,
                  pool,
                  analysisType,
                  currentVOI,
                  mainBranchIds,
                  branchId: idx,
                  lesionRanges: leslist,
                }).catch((err) => {
                  console.warn('[report][front_data] plaque main CPR capture failed', err);
                  return null;
                })
              : null;
          const img0 = serverImg0 ?? await cprlesioncapture(
            con,
            pool,
            idx,
            leslist,
            analysisType,
            currentVOI,
          );

          const img = await Promise.all(
            img0.map(async (imageGroup) =>
              await Promise.all(imageGroup.map((src) => rotateImage(src)))
            )
          );

          const lmdata = branchdataset.getPlaqueTotalInfo(
            idx,
            0,
            branchdataset.getLMCount() / 2,
            branchdataset.getLMCount(),
          );

          if (
            !maindata.lmTableData &&
            (nameTemp === 'LAD' || nameTemp === 'LCX')
          ) {
            maindata.lmTableData = buildMainTableData(lmdata);
          }
          maindata.lesions.push({
            name:
              nameTemp === 'LAD' || nameTemp === 'LCX'
                ? nameTemp + ' + LM'
                : nameTemp,
            images: [img[0][0]],
            tableData: {
              main: buildMainTableData(datamain),
            },
          });
        }
        plaquedatas.push(maindata);
        for (const les of status) {
          const datamain = branchdataset.getPlaqueTotalInfo(les.vesselIndex);

          const base: PlaqueVesselData = {
            vesselName: les.vesselName,
            lesions: [
              {
                name: les.vesselName,
                images: ['', ''],
                tableData: {
                  main: {
                    'Length (mm)': safe(datamain?.lengthValue),
                    'Lumen Volume (mm³)': safe(datamain?.lumenValue),
                    'Plaque Volume (mm³)': safe(datamain?.plaqueValue),
                    'Vessel Volume (mm³)': safe(datamain?.vesselValue),
                    'Calcified Plaque Volume (mm³, %)':
                      datamain?.calcifiedValue != null && datamain?.vesselValue
                        ? `${round(datamain.calcifiedValue)} (${round((datamain.calcifiedValue / datamain.vesselValue) * 100)})`
                        : '-',
                    'Noncalcified Plaque Volume (mm³, %)':
                      datamain?.noncalcifiedValue != null &&
                      datamain?.vesselValue
                        ? `${round(datamain.noncalcifiedValue)} (${round((datamain.noncalcifiedValue / datamain.vesselValue) * 100)})`
                        : '-',
                    'Low Attenuation Plaque Volume (mm³, %)':
                      datamain?.lowattenuationValue != null &&
                      datamain?.vesselValue
                        ? `${round(datamain.lowattenuationValue)} (${round((datamain.lowattenuationValue / datamain.vesselValue) * 100)})`
                        : '-',
                    'Percent Atheroma Volume (%)':
                      datamain?.plaqueburden != null
                        ? round(datamain.plaqueburden * 100)
                        : null,
                    'Remodeling Index': '-',
                    // 'Stenosis Degree (%)': '-',
                    'Minimum Lumen Area (mm²)': '-',
                    'Minimum Lumen Diameter (mm)': '-',
                    'Plaque Burden': '-',
                  },
                },
              },
            ],
          };

          const lesionItems: LesionData[] = [];
          let leslist: [number, number][] = [];
          for (const ls of les.lesions) {
            leslist.push([ls.value[0], ls.value[2]]);
          }
          const serverImg0 =
            getConfiguredCprCrossSectionImageMode() === 'server'
              ? await captureServerCprLesionImages({
                  accessToken,
                  seriesId: selectedSeriesId,
                  pool,
                  analysisType,
                  currentVOI,
                  mainBranchIds,
                  branchId: les.vesselIndex,
                  lesionRanges: leslist,
                }).catch((err) => {
                  console.warn('[report][front_data] plaque lesion CPR capture failed', err);
                  return null;
                })
              : null;
          const img0 = serverImg0 ?? await cprlesioncapture(
            con,
            pool,
            les.vesselIndex,
            leslist,
            analysisType,
            currentVOI,
          );
          const img = await Promise.all(
            img0.map(async (imageGroup) =>
              await Promise.all(imageGroup.map((src) => rotateImage(src)))
            )
          );
          for (let i = 0; i < les.lesions.length; i++) {
            const lsn = les.lesions[i];
            // 범위 보정
            const data = branchdataset.getPlaqueTotalInfo(
              les.vesselIndex,
              lsn.value[0] - configStore.get().coronary_front_expand,
              lsn.value[1] - configStore.get().coronary_front_expand,
              lsn.value[2] - configStore.get().coronary_front_expand,
            );

            // ★ 순차 실행 포인트: 이전 캡처가 끝난 뒤 다음 캡처 실행

            const imageData = [img[0][i], img[1][i]] as [string, string];

            lesionItems.push({
              name: lsn.name,
              images: imageData,
              tableData: {
                main: {
                  'Length (mm)': safe(data?.lengthValue),
                  'Lumen Volume (mm³)': safe(data?.lumenValue),
                  'Plaque Volume (mm³)': safe(data?.plaqueValue),
                  'Vessel Volume (mm³)': safe(data?.vesselValue),
                  'Calcified Plaque Volume (mm³, %)':
                    data?.calcifiedValue != null && data?.vesselValue
                      ? `${round(data.calcifiedValue)} (${round((data.calcifiedValue / data.vesselValue) * 100)})`
                      : '-',
                  'Noncalcified Plaque Volume (mm³, %)':
                    data?.noncalcifiedValue != null && data?.vesselValue
                      ? `${round(data.noncalcifiedValue)} (${round((data.noncalcifiedValue / data.vesselValue) * 100)})`
                      : '-',
                  'Low Attenuation Plaque Volume (mm³, %)':
                    data?.lowattenuationValue != null && data?.vesselValue
                      ? `${round(data.lowattenuationValue)} (${round((data.lowattenuationValue / data.vesselValue) * 100)})`
                      : '-',
                  'Percent Atheroma Volume (%)': '-',
                  'Remodeling Index': safe(data?.remodelingIndex),
                  // 'Stenosis Degree (%)':
                  //   data?.stenosisDegree != null
                  //     ? Math.round((1 - data.stenosisDegree) * 100)
                  //     : null,
                  'Minimum Lumen Area (mm²)': safe(data?.minLumenArea),
                  'Minimum Lumen Diameter (mm)': safe(data?.minLumenDiameter),
                  'Plaque Burden':
                    data?.plaqueburden != null
                      ? round(data.plaqueburden * 100)
                      : null,
                },
              },
            });
          }

          plaquedatas.push({
            ...base,
            lesions: [...base.lesions, ...lesionItems],
          });
        }

        await cprlesioncapture(
          con,
          pool,
          con.current?.selectedBranchNum!,
          [[0, 0]],
          analysisType,
          currentVOI,
          false,
        );
        // ------- 순차 실행 끝 -------

        if (!cancelled) setDataset(plaquedatas);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          closeLoadingModal();
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [branchdataset, bufferPool, pool]);

  const nextFrame = () =>
    new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

  const waitForPaint = async () => {
    // 두 번 정도 프레임 대기하면 레이아웃/스타일 적용이 안정적
    await nextFrame();
    await nextFrame();
  };

  const captureClick = async () => {
    if (!dataset) return;
    let buffers: BufferData[] = [];
    for (let i = 0; i < dataset?.length; i++) {
      setselectedindex(i);
      await waitForPaint();
      let url = await screenshotElement(ref.current!);

      if (url) {
        const buffer = await imageUrlToArrayBufferSimple(url);
        buffers.push(buffer);
      }
    }

    let name = analysisType + ' Report';
    const data = {
      buffers: buffers,
      screen_capture: true,
      capture_kind: 'Report',
      series_description: name,
    };

    AutoCaptureModalOpen('reportCapture', [data]);
  };

  const capture3DForPdf = async (): Promise<string | undefined> => {
    const keyname = selectedLayoutConfig?.panelConfig?.some(
      (panel) => panel.componentKey === 'Surface3D',
    )
      ? 'Surface3D'
      : 'VR';

    const d3context = poolRef.current[keyname as ContextKey] as
      | SurfaceContext
      | VRContext
      | undefined;
    if (!d3context) return undefined;

    const restoreReport3DOverlayActors = hideReport3DOverlayActors(d3context);
    try {
      const url = await captureSurface3D(d3context, analysisType, pool);
      return url ? await cropToSquare(url) : undefined;
    } finally {
      restoreReport3DOverlayActors();
    }
  };

  const buildPatientInfo = (): PlaquePdfPatientInfo => ({
    scanDate: selectedSeries?.studyDate,
    patientId: selectedSeries?.patient?.patientId,
    patientName: selectedSeries?.patient?.patientName,
    patientSex: selectedSeries?.patient?.patientSex,
    patientBirthdate: selectedSeries?.patient?.patientBirthdate,
    patientAge: selectedSeries?.patient?.patientAge,
  });

  const buildExportInput = async (): Promise<{
    patientInfo: PlaquePdfPatientInfo;
    reportData: PlaqueVesselData[];
    threeDImage?: string;
    agatstonScore?: number[];
  }> => {
    const patientInfo = buildPatientInfo();
    const threeDImage = await capture3DForPdf().catch((err) => {
      console.warn('[report][export] plaque 3D capture failed', err);
      return undefined;
    });

    return {
      patientInfo,
      reportData: dataset ?? [],
      threeDImage,
      agatstonScore: configStore.get().agatstonScore,
    };
  };

  const prepareReportPreview = useCallback(async () => {
    if (!dataset || isPreparingPreview) return;

    setIsPreparingPreview(true);
    try {
      const exportInput = await buildExportInput();
      const pageUrls = await createPlaqueAnalysisPageImageUrls(exportInput);
      setPreviewPageUrls(pageUrls);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to generate plaque report preview.';
      await errorAction(err as any, message);
      setReportViewMode('classic');
    } finally {
      setIsPreparingPreview(false);
    }
  }, [dataset, isPreparingPreview]);

  const handleToggleReportMode = async () => {
    if (reportViewMode === 'preview') {
      setReportViewMode('classic');
      return;
    }

    setReportViewMode('preview');
    if (!previewPageUrls.length) {
      await prepareReportPreview();
    }
  };

  const handlePrintPreview = useCallback(async () => {
    if (!previewPageUrls.length || isPreparingPreview) return;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    const printDocument = iframe.contentDocument;
    const printWindow = iframe.contentWindow;
    if (!printDocument || !printWindow) {
      iframe.remove();
      return;
    }

    const pagesHtml = previewPageUrls
      .map(
        (url, index) => `
          <section class="page">
            <img src="${escapePrintAttribute(url)}" alt="Plaque report page ${index + 1}" />
          </section>`,
      )
      .join('');

    printDocument.open();
    printDocument.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Plaque Report Preview</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 0;
    }
    * {
      box-sizing: border-box;
    }
    html,
    body {
      width: 210mm;
      min-height: 297mm;
      margin: 0;
      padding: 0;
      background: #ffffff;
    }
    .page {
      width: 210mm;
      height: 297mm;
      margin: 0;
      padding: 0;
      overflow: hidden;
      page-break-after: always;
      break-after: page;
      background: #ffffff;
    }
    .page:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .page img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: fill;
    }
  </style>
</head>
<body>${pagesHtml}</body>
</html>`);
    printDocument.close();

    const images = Array.from(printDocument.images);
    await Promise.all(
      images.map(
        (image) =>
          image.complete
            ? Promise.resolve()
            : new Promise<void>((resolve) => {
                image.onload = () => resolve();
                image.onerror = () => resolve();
              }),
      ),
    );
    await nextFrame();

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 1000);
    };
    printWindow.addEventListener('afterprint', cleanup, { once: true });
    setTimeout(cleanup, 60000);
    printWindow.focus();
    printWindow.print();
  }, [previewPageUrls, isPreparingPreview]);

  // 계산 완료 전에는 렌더하지 않음.
  // 모든 Hook 선언 이후에 return 해야 Hook order mismatch가 발생하지 않는다.
  if (isLoading) return null;

  return (
    <BaseModalContent
      className={reportViewMode === 'preview' ? 'plaquePreview' : 'plaque'}
      refelement={ref}
      onCapture={captureClick}
      capturebutton={reportViewMode === 'classic'}
      headerActions={
        <>
          {/* <button
            type="button"
            className={`${styles['export-button']} ${
              reportViewMode === 'preview' ? styles['mode-active'] : ''
            }`}
            data-capture-exclude="true"
            disabled={!dataset || isPreparingPreview}
            onClick={handleToggleReportMode}
          >
            {reportViewMode === 'preview' ? 'Analysis' : 'Preview'}
          </button>
          {reportViewMode === 'preview' && (
            <button
              type="button"
              className={styles['print-button']}
              data-capture-exclude="true"
              disabled={!previewPageUrls.length || isPreparingPreview}
              onClick={handlePrintPreview}
              title="Print"
              aria-label="Print plaque report"
            >
              <img src={printIconUrl} alt="" draggable={false} />
            </button>
          )} */}
        </>
      }
      onClose={onClose}
      // vw={90}
      // vh={59}
      title={reportViewMode === 'preview' ? 'Plaque Report Preview' : names.title}
    >
      {reportViewMode === 'preview' ? (
        <div className={styles['a4-preview-container']}>
          {isPreparingPreview && (
            <div className={styles['a4-preview-loading']}>
              Generating report preview…
            </div>
          )}
          {!isPreparingPreview &&
            previewPageUrls.map((pageUrl, index) => (
              <img
                key={`${pageUrl.slice(0, 32)}-${index}`}
                className={styles['a4-preview-page']}
                src={pageUrl}
                alt={`Plaque report page ${index + 1}`}
                draggable={false}
              />
            ))}
        </div>
      ) : (
        <PlaqueReportModalContent
          data={dataset}
          index={selectedindex}
          onConfirm={(index) => {
            setselectedindex(index);
          }}
        />
      )}
    </BaseModalContent>
  );
};

export function usePlaqueReportModal(res: {
  updateActivatedTool: (tool: any, check: boolean) => void;
}) {
  return useReportModal({ Component: PlaqueReportModal, ...res });
}
